import type { EntityId } from '../api/entityId';

export interface EditorViewportPosition {
  scrollTop: number;
  selectionAnchor: number;
  selectionHead: number;
}

export interface EditorTabState {
  documentId: EntityId;
  paneIndex: number;
  title: string;
  projectName: string;
  contentVersion: number;
  sortOrder: number;
}

export interface EditorPaneState {
  paneIndex: number;
  activeDocumentId: EntityId | null;
  tabs: EditorTabState[];
}

export interface EditorTabsState {
  activePaneIndex: number;
  panes: EditorPaneState[];
}

export interface WorkspaceTreeExpansionState {
  projectIds: EntityId[];
  folderIds: EntityId[];
}

const sidebarScrollKeyPrefix = 'knowledgebase.sidebarScrollTop.v1:';
const sidebarWidthKeyPrefix = 'knowledgebase.sidebarWidth.v1:';
const editorViewportKeyPrefix = 'knowledgebase.editorViewport.v1:';
const editorTabsKeyPrefix = 'knowledgebase.editorTabs.v1:';
const workspaceTreeExpansionKeyPrefix = 'knowledgebase.workspaceTreeExpansion.v1:';
const recentDocumentsKeyPrefix = 'knowledgebase.recentDocuments.v1:';
const commentsPanelOpenKeyPrefix = 'knowledgebase.commentsPanelOpen.v1:';
const commentsPanelWidthKeyPrefix = 'knowledgebase.commentsPanelWidth.v1:';
const recentDocumentsLimit = 100;

export function getSidebarScrollTop(userId: EntityId): number | null {
  const value = window.localStorage.getItem(getSidebarScrollKey(userId));
  if (value == null) {
    return null;
  }

  const scrollTop = Number(value);
  if (!Number.isFinite(scrollTop) || scrollTop < 0) {
    window.localStorage.removeItem(getSidebarScrollKey(userId));
    return null;
  }

  return scrollTop;
}

export function setSidebarScrollTop(userId: EntityId, scrollTop: number): void {
  window.localStorage.setItem(getSidebarScrollKey(userId), String(normalizeScrollTop(scrollTop)));
}

export function getSidebarWidth(userId: EntityId): number | null {
  const key = getSidebarWidthKey(userId);
  const storedValue = window.localStorage.getItem(key);
  if (storedValue == null) {
    return null;
  }

  const value = Number(storedValue);
  if (!Number.isFinite(value) || value <= 0) {
    window.localStorage.removeItem(key);
    return null;
  }

  return Math.round(value);
}

export function setSidebarWidth(userId: EntityId, width: number): void {
  if (!Number.isFinite(width) || width <= 0) {
    throw new Error('Sidebar width must be a positive number.');
  }

  window.localStorage.setItem(getSidebarWidthKey(userId), String(Math.round(width)));
}

export function getCommentsPanelOpen(userId: EntityId): boolean | null {
  const key = getCommentsPanelOpenKey(userId);
  const storedValue = window.localStorage.getItem(key);
  if (storedValue == null) {
    return null;
  }

  if (storedValue !== 'open' && storedValue !== 'closed') {
    window.localStorage.removeItem(key);
    return null;
  }

  return storedValue === 'open';
}

export function setCommentsPanelOpen(userId: EntityId, open: boolean): void {
  window.localStorage.setItem(getCommentsPanelOpenKey(userId), open ? 'open' : 'closed');
}

export function getCommentsPanelWidth(userId: EntityId): number | null {
  const key = getCommentsPanelWidthKey(userId);
  const storedValue = window.localStorage.getItem(key);
  if (storedValue == null) {
    return null;
  }

  const value = Number(storedValue);
  if (!Number.isFinite(value) || value <= 0) {
    window.localStorage.removeItem(key);
    return null;
  }

  return Math.round(value);
}

export function setCommentsPanelWidth(userId: EntityId, width: number): void {
  if (!Number.isFinite(width) || width <= 0) {
    throw new Error('Comments panel width must be a positive number.');
  }

  window.localStorage.setItem(getCommentsPanelWidthKey(userId), String(Math.round(width)));
}

export function getEditorViewportPosition(
  userId: EntityId,
  paneIndex: number,
  documentId: EntityId
): EditorViewportPosition | null {
  const key = getEditorViewportKey(userId, paneIndex, documentId);
  const value = window.localStorage.getItem(key);
  if (value == null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }

  if (!isEditorViewportPosition(parsed)) {
    window.localStorage.removeItem(key);
    return null;
  }

  return parsed;
}

export function setEditorViewportPosition(
  userId: EntityId,
  paneIndex: number,
  documentId: EntityId,
  position: EditorViewportPosition
): void {
  window.localStorage.setItem(
    getEditorViewportKey(userId, paneIndex, documentId),
    JSON.stringify(normalizeEditorViewportPosition(position))
  );
}

export function getEditorTabsState(userId: EntityId): EditorTabsState | null {
  const key = getEditorTabsKey(userId);
  const value = window.localStorage.getItem(key);
  if (value == null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }

  if (!isEditorTabsState(parsed)) {
    window.localStorage.removeItem(key);
    return null;
  }

  return parsed;
}

export function setEditorTabsState(userId: EntityId, state: EditorTabsState): void {
  window.localStorage.setItem(getEditorTabsKey(userId), JSON.stringify(normalizeEditorTabsState(state)));
}

export function getWorkspaceTreeExpansionState(userId: EntityId): WorkspaceTreeExpansionState | null {
  const key = getWorkspaceTreeExpansionKey(userId);
  const value = window.localStorage.getItem(key);
  if (value == null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }

  if (!isWorkspaceTreeExpansionState(parsed)) {
    window.localStorage.removeItem(key);
    return null;
  }

  return parsed;
}

export function setWorkspaceTreeExpansionState(userId: EntityId, state: WorkspaceTreeExpansionState): void {
  window.localStorage.setItem(
    getWorkspaceTreeExpansionKey(userId),
    JSON.stringify(normalizeWorkspaceTreeExpansionState(state))
  );
}

export function getRecentDocuments(userId: EntityId): EntityId[] {
  const key = getRecentDocumentsKey(userId);
  const value = window.localStorage.getItem(key);
  if (value == null) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    window.localStorage.removeItem(key);
    return [];
  }

  if (!Array.isArray(parsed) || !parsed.every(isEntityId)) {
    window.localStorage.removeItem(key);
    return [];
  }

  return parsed;
}

export function recordRecentDocument(userId: EntityId, documentId: EntityId): void {
  const existing = getRecentDocuments(userId).filter(id => id !== documentId);
  existing.unshift(documentId);
  window.localStorage.setItem(
    getRecentDocumentsKey(userId),
    JSON.stringify(existing.slice(0, recentDocumentsLimit))
  );
}

function getRecentDocumentsKey(userId: EntityId): string {
  return `${recentDocumentsKeyPrefix}${userId}`;
}

function getSidebarScrollKey(userId: EntityId): string {
  return `${sidebarScrollKeyPrefix}${userId}`;
}

function getSidebarWidthKey(userId: EntityId): string {
  return `${sidebarWidthKeyPrefix}${userId}`;
}

function getCommentsPanelOpenKey(userId: EntityId): string {
  return `${commentsPanelOpenKeyPrefix}${userId}`;
}

function getCommentsPanelWidthKey(userId: EntityId): string {
  return `${commentsPanelWidthKeyPrefix}${userId}`;
}

function getEditorViewportKey(userId: EntityId, paneIndex: number, documentId: EntityId): string {
  return `${editorViewportKeyPrefix}${userId}:${paneIndex}:${documentId}`;
}

function getEditorTabsKey(userId: EntityId): string {
  return `${editorTabsKeyPrefix}${userId}`;
}

function getWorkspaceTreeExpansionKey(userId: EntityId): string {
  return `${workspaceTreeExpansionKeyPrefix}${userId}`;
}

function isEditorViewportPosition(value: unknown): value is EditorViewportPosition {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return isScrollTop(candidate.scrollTop)
    && isSelectionPosition(candidate.selectionAnchor)
    && isSelectionPosition(candidate.selectionHead);
}

function isEditorTabsState(value: unknown): value is EditorTabsState {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return isPaneIndex(candidate.activePaneIndex)
    && Array.isArray(candidate.panes)
    && candidate.panes.every(isEditorPaneState);
}

function isEditorPaneState(value: unknown): value is EditorPaneState {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return isPaneIndex(candidate.paneIndex)
    && (candidate.activeDocumentId == null || isEntityId(candidate.activeDocumentId))
    && Array.isArray(candidate.tabs)
    && candidate.tabs.every(isEditorTabState);
}

function isEditorTabState(value: unknown): value is EditorTabState {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return isEntityId(candidate.documentId)
    && isPaneIndex(candidate.paneIndex)
    && typeof candidate.title === 'string'
    && typeof candidate.projectName === 'string'
    && typeof candidate.contentVersion === 'number'
    && Number.isInteger(candidate.contentVersion)
    && typeof candidate.sortOrder === 'number'
    && Number.isInteger(candidate.sortOrder)
    && candidate.sortOrder >= 0;
}

function isWorkspaceTreeExpansionState(value: unknown): value is WorkspaceTreeExpansionState {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return Array.isArray(candidate.projectIds)
    && candidate.projectIds.every(isEntityId)
    && Array.isArray(candidate.folderIds)
    && candidate.folderIds.every(isEntityId);
}

function normalizeEditorViewportPosition(position: EditorViewportPosition): EditorViewportPosition {
  return {
    scrollTop: normalizeScrollTop(position.scrollTop),
    selectionAnchor: normalizeSelectionPosition(position.selectionAnchor),
    selectionHead: normalizeSelectionPosition(position.selectionHead)
  };
}

function normalizeEditorTabsState(state: EditorTabsState): EditorTabsState {
  return {
    activePaneIndex: normalizePaneIndex(state.activePaneIndex),
    panes: state.panes.map(normalizeEditorPaneState)
  };
}

function normalizeEditorPaneState(pane: EditorPaneState): EditorPaneState {
  return {
    paneIndex: normalizePaneIndex(pane.paneIndex),
    activeDocumentId: pane.activeDocumentId,
    tabs: pane.tabs.map(tab => ({
      documentId: tab.documentId,
      paneIndex: normalizePaneIndex(tab.paneIndex),
      title: tab.title,
      projectName: tab.projectName,
      contentVersion: tab.contentVersion,
      sortOrder: Math.max(0, Math.trunc(tab.sortOrder))
    }))
  };
}

function normalizeWorkspaceTreeExpansionState(state: WorkspaceTreeExpansionState): WorkspaceTreeExpansionState {
  return {
    projectIds: uniqueEntityIds(state.projectIds),
    folderIds: uniqueEntityIds(state.folderIds)
  };
}

function uniqueEntityIds(ids: EntityId[]): EntityId[] {
  return [...new Set(ids)];
}

function isScrollTop(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isPaneIndex(value: unknown): value is number {
  return value === 0 || value === 1;
}

function isEntityId(value: unknown): value is EntityId {
  return typeof value === 'string' && /^\d+$/.test(value);
}

function isSelectionPosition(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function normalizeScrollTop(value: number): number {
  return Math.max(0, value);
}

function normalizeSelectionPosition(value: number): number {
  return Math.max(0, Math.trunc(value));
}

function normalizePaneIndex(value: number): number {
  return value === 1 ? 1 : 0;
}
